import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCircle, AlertTriangle, Info, Clock, Trash2, Check, Filter, ArrowLeft,
  Brain, Sparkles, Send, RefreshCw, TrendingUp, TrendingDown, Shield, Target,
  ChevronDown, ChevronUp, Zap, Eye, EyeOff, Volume2, VolumeX, Settings
} from 'lucide-react';
import { scans as scansApi } from '../../api/client';
import { useI18n } from '../../i18n';

const NotificationsList = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // AI Assistant states
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Settings states
  const [showSettings, setShowSettings] = useState(false);
  const [notifSettings, setNotifSettings] = useState({
    soundEnabled: true,
    criticalOnly: false,
    autoMarkRead: false
  });

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await scansApi.list(0, 100);
      const scansArray = data.scans || [];

      const newNotifications = [];

      scansArray.forEach(scan => {
        if (scan.status === 'completed' && scan.total_vulnerabilities > 0) {
          newNotifications.push({
            id: `scan-${scan.id}`,
            type: scan.critical_count > 0 ? 'critical' : 'warning',
            title: scan.critical_count > 0
              ? t('notifications.criticalVulnsFound', { count: scan.critical_count })
              : t('notifications.vulnsDetected', { count: scan.total_vulnerabilities }),
            message: t('notifications.hostsScanned', { count: scan.total_hosts_found || 0, target: scan.target }),
            timestamp: new Date(scan.completed_at || scan.created_at),
            read: false,
            scanId: scan.id,
            data: {
              target: scan.target,
              vulns: scan.total_vulnerabilities,
              critical: scan.critical_count,
              hosts: scan.total_hosts_found
            }
          });
        }

        if (scan.status === 'completed' && scan.total_vulnerabilities === 0) {
          newNotifications.push({
            id: `scan-success-${scan.id}`,
            type: 'success',
            title: t('notifications.scanCompletedNoVulns'),
            message: t('notifications.noVulnsDetected', { target: scan.target }),
            timestamp: new Date(scan.completed_at || scan.created_at),
            read: true,
            scanId: scan.id,
            data: { target: scan.target, vulns: 0, hosts: scan.total_hosts_found }
          });
        }

        if (scan.status === 'running') {
          newNotifications.push({
            id: `running-${scan.id}`,
            type: 'info',
            title: t('notifications.scanInProgress'),
            message: t('notifications.analyzing', { target: scan.target }),
            timestamp: new Date(scan.started_at || scan.created_at),
            read: true,
            scanId: scan.id,
            data: { target: scan.target }
          });
        }

        if (scan.status === 'failed') {
          newNotifications.push({
            id: `failed-${scan.id}`,
            type: 'critical',
            title: t('notifications.scanFailed'),
            message: t('notifications.errorOn', { target: scan.target }),
            timestamp: new Date(scan.completed_at || scan.created_at),
            read: false,
            scanId: scan.id,
            data: { target: scan.target, error: true }
          });
        }
      });

      newNotifications.sort((a, b) => b.timestamp - a.timestamp);
      setNotifications(newNotifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id, e) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // AI Assistant functions
  const generateLocalAIResponse = useCallback((query, notifs) => {
    const q = query.toLowerCase();
    const criticalCount = notifs.filter(n => n.type === 'critical').length;
    const unreadCount = notifs.filter(n => !n.read).length;
    const successCount = notifs.filter(n => n.type === 'success').length;
    const totalVulns = notifs.reduce((sum, n) => sum + (n.data?.vulns || 0), 0);

    if (q.includes('résumé') || q.includes('resume') || q.includes('synthèse')) {
      return `📊 **Résumé des notifications**

Vous avez **${notifs.length}** notifications au total :
- 🔴 **${criticalCount}** critiques nécessitant une attention immédiate
- 📬 **${unreadCount}** non lues
- ✅ **${successCount}** scans réussis sans problème

${criticalCount > 0 ? `⚠️ **Action recommandée** : Traitez en priorité les ${criticalCount} alertes critiques qui peuvent indiquer des vulnérabilités exploitables.` : '✅ Aucune alerte critique en cours.'}`;
    }

    if (q.includes('critique') || q.includes('urgent') || q.includes('priorit')) {
      const criticals = notifs.filter(n => n.type === 'critical');
      if (criticals.length === 0) {
        return `✅ **Aucune notification critique**

Votre infrastructure ne présente pas d'alerte urgente actuellement. Continuez à surveiller régulièrement.`;
      }
      return `🚨 **${criticals.length} Notification(s) critique(s)**

${criticals.slice(0, 5).map(n => `- **${n.title}** sur ${n.data?.target || 'cible inconnue'}
  ${n.message}`).join('\n\n')}

**Actions recommandées** :
1. Analysez chaque vulnérabilité critique
2. Appliquez les correctifs disponibles
3. Isolez les systèmes compromis si nécessaire`;
    }

    if (q.includes('vulnérabilité') || q.includes('vuln') || q.includes('faille')) {
      return `🔍 **Analyse des vulnérabilités**

Total détecté : **${totalVulns}** vulnérabilités
Critiques : **${notifs.filter(n => n.data?.critical > 0).reduce((sum, n) => sum + n.data.critical, 0)}**

**Recommandations** :
1. Priorisez les vulnérabilités avec score CVSS > 7
2. Vérifiez si des exploits publics existent
3. Planifiez les correctifs par ordre de criticité
4. Documentez les exceptions si nécessaire`;
    }

    if (q.includes('aide') || q.includes('help') || q.includes('quoi faire')) {
      return `🤖 **Assistant Notifications - Aide**

Je peux vous aider à :
- **Résumer** vos notifications
- **Analyser** les alertes critiques
- **Prioriser** les actions à entreprendre
- **Expliquer** les types de notifications

**Exemples de questions** :
- "Donne-moi un résumé"
- "Quelles sont les alertes critiques ?"
- "Que signifient ces vulnérabilités ?"
- "Comment gérer les notifications ?"`;
    }

    if (q.includes('gérer') || q.includes('configurer') || q.includes('paramètre')) {
      return `⚙️ **Gestion des notifications**

**Bonnes pratiques** :
1. ✅ Marquez les notifications traitées comme lues
2. 🔔 Activez les alertes sonores pour les critiques
3. 📋 Créez des règles de filtrage personnalisées
4. 🗑️ Purgez régulièrement les anciennes notifications

**Conseil** : Consultez les paramètres (icône ⚙️) pour personnaliser vos préférences.`;
    }

    return `🤖 **Analyse de votre demande**

Je comprends que vous souhaitez en savoir plus sur "${query}".

**État actuel** :
- ${notifs.length} notifications totales
- ${unreadCount} non lues
- ${criticalCount} critiques

Posez-moi des questions comme :
- "Résume mes notifications"
- "Montre les alertes critiques"
- "Comment gérer mes alertes ?"`;
  }, []);

  const handleAIQuery = async () => {
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    setAiResponse('');

    // Simulate AI response with local logic
    setTimeout(() => {
      const response = generateLocalAIResponse(aiQuery, notifications);
      setAiResponse(response);
      setAiLoading(false);
    }, 800);
  };

  const quickAIPrompts = [
    { label: 'Résumé', query: 'Donne-moi un résumé de mes notifications' },
    { label: 'Critiques', query: 'Quelles sont les alertes critiques ?' },
    { label: 'Actions', query: 'Que dois-je faire en priorité ?' },
    { label: 'Aide', query: 'Comment utiliser les notifications ?' },
  ];

  const getIcon = (type) => {
    switch(type) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      warning: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      success: 'bg-green-500/20 text-green-400 border-green-500/50',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
    };
    const labels = {
      critical: t('notifications.typeCritical'),
      warning: t('notifications.typeWarning'),
      success: t('notifications.typeSuccess'),
      info: t('notifications.typeInfo')
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded border ${styles[type] || styles.info}`}>
        {labels[type] || 'Info'}
      </span>
    );
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('notifications.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t('notifications.daysAgo', { count: days });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const filteredNotifications = filter === 'all'
    ? notifications
    : filter === 'unread'
      ? notifications.filter(n => !n.read)
      : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => !n.read).length;
  const criticalCount = notifications.filter(n => n.type === 'critical').length;
  const warningCount = notifications.filter(n => n.type === 'warning').length;
  const successCount = notifications.filter(n => n.type === 'success').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 bg-brand-black-light border border-gray-700 hover:border-brand-green/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-brand-green" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gradient-green flex items-center gap-3">
                <Bell className="w-8 h-8" />
                {t('nav.notifications')}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {t('notifications.managementCenter')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                showAIPanel
                  ? 'bg-brand-green text-black'
                  : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
              }`}
            >
              <Brain className="w-4 h-4" />
              <span>{t('notifications.aiAssistant')}</span>
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2.5 bg-brand-black-light border border-gray-700 hover:border-brand-green/50 rounded-lg text-gray-400 hover:text-brand-green transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={fetchNotifications}
              className="p-2.5 bg-brand-black-light border border-brand-green/50 rounded-lg text-brand-green hover:bg-brand-green/10 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-brand-black-light rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase">{t('notifications.total')}</div>
                <div className="text-2xl font-bold text-white">{notifications.length}</div>
              </div>
              <Bell className="w-8 h-8 text-gray-600" />
            </div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 border border-blue-500/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase">{t('notifications.unread')}</div>
                <div className="text-2xl font-bold text-blue-400">{unreadCount}</div>
              </div>
              <Eye className="w-8 h-8 text-blue-500/50" />
            </div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 border border-red-500/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase">{t('notifications.critical')}</div>
                <div className="text-2xl font-bold text-red-400">{criticalCount}</div>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500/50" />
            </div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 border border-orange-500/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase">{t('notifications.warning')}</div>
                <div className="text-2xl font-bold text-orange-400">{warningCount}</div>
              </div>
              <Zap className="w-8 h-8 text-orange-500/50" />
            </div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 border border-green-500/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase">{t('notifications.success')}</div>
                <div className="text-2xl font-bold text-green-400">{successCount}</div>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500/50" />
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAIPanel && (
          <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-xl border border-purple-500/30 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-purple-400" />
              <h3 className="font-bold text-purple-400">{t('notifications.aiAssistantTitle')}</h3>
              <Sparkles className="w-4 h-4 text-purple-400 animate-pulse" />
            </div>

            {/* Quick prompts */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quickAIPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setAiQuery(prompt.query);
                    setTimeout(() => {
                      setAiLoading(true);
                      setTimeout(() => {
                        setAiResponse(generateLocalAIResponse(prompt.query, notifications));
                        setAiLoading(false);
                      }, 600);
                    }, 100);
                  }}
                  className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-lg text-sm text-purple-300 hover:bg-purple-500/30 hover:border-purple-500/50 transition-all"
                >
                  {prompt.label}
                </button>
              ))}
            </div>

            {/* Query input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAIQuery()}
                placeholder={t('notifications.aiPlaceholder')}
                className="flex-1 px-4 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
              />
              <button
                onClick={handleAIQuery}
                disabled={aiLoading || !aiQuery.trim()}
                className="px-4 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {aiLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* AI Response */}
            {aiResponse && (
              <div className="bg-brand-black/50 rounded-lg p-4 border border-purple-500/20">
                <div className="prose prose-invert prose-sm max-w-none">
                  {aiResponse.split('\n').map((line, idx) => (
                    <p key={idx} className={`mb-2 ${line.startsWith('**') ? 'font-semibold text-white' : 'text-gray-300'}`}>
                      {line.replace(/\*\*/g, '')}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-brand-black-light rounded-xl border border-gray-700 p-5">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-brand-green" />
              {t('notifications.settings')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800">
                <div className="flex items-center gap-3">
                  {notifSettings.soundEnabled ? <Volume2 className="w-5 h-5 text-brand-green" /> : <VolumeX className="w-5 h-5 text-gray-500" />}
                  <span className="text-gray-300">{t('notifications.soundEnabled')}</span>
                </div>
                <input
                  type="checkbox"
                  checked={notifSettings.soundEnabled}
                  onChange={(e) => setNotifSettings(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  className="w-5 h-5 accent-brand-green"
                />
              </label>
              <label className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-5 h-5 ${notifSettings.criticalOnly ? 'text-red-400' : 'text-gray-500'}`} />
                  <span className="text-gray-300">{t('notifications.criticalOnly')}</span>
                </div>
                <input
                  type="checkbox"
                  checked={notifSettings.criticalOnly}
                  onChange={(e) => setNotifSettings(prev => ({ ...prev, criticalOnly: e.target.checked }))}
                  className="w-5 h-5 accent-brand-green"
                />
              </label>
              <label className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800">
                <div className="flex items-center gap-3">
                  <Check className={`w-5 h-5 ${notifSettings.autoMarkRead ? 'text-brand-green' : 'text-gray-500'}`} />
                  <span className="text-gray-300">{t('notifications.autoMarkRead')}</span>
                </div>
                <input
                  type="checkbox"
                  checked={notifSettings.autoMarkRead}
                  onChange={(e) => setNotifSettings(prev => ({ ...prev, autoMarkRead: e.target.checked }))}
                  className="w-5 h-5 accent-brand-green"
                />
              </label>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4 sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-brand-green" />
                <h3 className="font-bold text-white">{t('notifications.filters')}</h3>
              </div>
              <div className="space-y-2">
                {[
                  { key: 'all', label: t('common.all'), count: notifications.length, color: 'gray' },
                  { key: 'unread', label: t('notifications.unread'), count: unreadCount, color: 'blue' },
                  { key: 'critical', label: t('notifications.critical'), count: criticalCount, color: 'red' },
                  { key: 'warning', label: t('notifications.warning'), count: warningCount, color: 'orange' },
                  { key: 'success', label: t('notifications.success'), count: successCount, color: 'green' },
                  { key: 'info', label: t('notifications.info'), count: notifications.filter(n => n.type === 'info').length, color: 'blue' },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                      filter === f.key
                        ? `bg-${f.color}-500/20 border border-${f.color}-500/50 text-${f.color}-400`
                        : 'bg-brand-black border border-gray-800 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span>{f.label}</span>
                    <span className={`text-sm px-2 py-0.5 rounded ${filter === f.key ? `bg-${f.color}-500/30` : 'bg-gray-800'}`}>
                      {f.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 pt-4 border-t border-gray-800 space-y-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-brand-black border border-brand-green/30 rounded-lg text-gray-400 hover:text-brand-green hover:border-brand-green/50 transition-all"
                  >
                    <Check className="w-4 h-4" />
                    <span>{t('notifications.markAllRead')}</span>
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-brand-black border border-red-500/30 rounded-lg text-gray-400 hover:text-red-400 hover:border-red-500/50 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>{t('notifications.clearAll')}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="lg:col-span-3 space-y-3">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => {
                    markAsRead(notif.id);
                    if (notif.scanId) {
                      navigate(`/scans/${notif.scanId}`);
                    }
                  }}
                  className={`bg-brand-black-light rounded-xl p-4 border transition-all duration-300 cursor-pointer group ${
                    notif.read
                      ? 'border-gray-800 hover:border-gray-700'
                      : 'border-brand-green/50 hover:border-brand-green shadow-glow-green'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                      notif.type === 'critical' ? 'bg-red-500/20' :
                      notif.type === 'warning' ? 'bg-orange-500/20' :
                      notif.type === 'success' ? 'bg-green-500/20' : 'bg-blue-500/20'
                    }`}>
                      {getIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className={`font-semibold ${notif.read ? 'text-gray-400' : 'text-white'}`}>
                          {notif.title}
                        </h3>
                        {!notif.read && (
                          <div className="w-2 h-2 rounded-full bg-brand-green animate-pulse"></div>
                        )}
                        {getTypeBadge(notif.type)}
                      </div>
                      <p className="text-gray-500 text-sm mb-2">{notif.message}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(notif.timestamp)}
                        </div>
                        {notif.data?.target && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {notif.data.target}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteNotification(notif.id, e)}
                      className="p-2 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-brand-black-light rounded-xl p-12 border border-brand-green/20 text-center">
                <Bell className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-300 mb-2">{t('notifications.noNotifications')}</h3>
                <p className="text-gray-500">
                  {filter === 'all'
                    ? t('notifications.noNotificationsDesc')
                    : filter === 'unread'
                      ? t('notifications.allRead')
                      : t('notifications.noNotificationsType', { type: filter })
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsList;
